// Single source of truth for outbound partner links.
//
// The BetterLife REF URL was previously hardcoded in five separate files with
// inconsistent protocols (one was plain http://), which meant no placement was
// attributable. Centralising it lets every call site carry UTM parameters so the
// funnel is measurable in PostHog, and lets members be routed to the page that
// carries their closing cost benefit.

import type { RefProduct } from '@/lib/roeRecommendation';

/** Members get $2,000 off closing costs, per the Resources page partner list. */
export const REF_MEMBER_BENEFIT = '$2,000 off closing costs';

const REF_BASE = 'https://betterliferef.com';
/** Member landing page that carries the First Deal closing cost benefit. */
const REF_MEMBER_BASE = 'https://betterliferef.com/tribe';

/** Where a placement lives, used as utm_content so placements are comparable. */
export type RefPlacement =
  | 'roe_recommendation'
  | 'roe_scenario'
  | 'roe_footer_banner'
  | 'roe_rate_pill'
  | 'roe_portfolio'
  | 'roe_quote_fallback'
  | 'mortgage_calculator_footer'
  | 'rental_analyzer_loan_step'
  | 'str_calculator_loan_step'
  | 'phase2_homework'
  | 'resources_partners';

interface RefLinkOptions {
  placement: RefPlacement;
  isMember?: boolean;
  /** Recommended product, passed through so REF can route the enquiry. */
  product?: RefProduct;
}

/**
 * Build an attributed BetterLife REF URL.
 *
 * Members are sent to the /tribe page so their closing cost benefit applies.
 * Everyone gets UTM parameters identifying the tool and the exact placement.
 */
export function refUrl({ placement, isMember = false, product }: RefLinkOptions): string {
  const url = new URL(isMember ? REF_MEMBER_BASE : REF_BASE);
  url.searchParams.set('utm_source', 'firstdeal');
  url.searchParams.set('utm_medium', 'referral');
  url.searchParams.set('utm_campaign', 'first_deal_tools');
  url.searchParams.set('utm_content', placement);
  if (product && product !== 'none') {
    url.searchParams.set('utm_term', product);
  }
  return url.toString();
}

/** Copy shown alongside the CTA, tailored to the product being recommended. */
export const REF_PRODUCT_BLURB: Record<RefProduct, string> = {
  'rate-term-refi':
    'Lower your payment without putting any capital in. Investor focused lending, built by investors.',
  'cash-out-refi':
    'Turn idle equity into deployable capital. Investor focused lending, built by investors.',
  'purchase-loan':
    'Finance the next one without touching the loan you already have. Rentals, bridge, portfolio and more.',
  'rate-quote':
    'Get a real number to compare against, not an estimate. Investor focused lending, built by investors.',
  none: 'Investor focused lending built by investors, for investors. Fix and flips, rentals, bridge, construction, portfolio and STR.',
};

/** Product lines REF advertises. Used in the banner supporting line. */
export const REF_PRODUCT_LINES = 'Fix & Flips • Rentals • Bridge • Construction • Portfolio • STR';
